import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    ArrowLeft, Phone, Mail, MapPin, BadgeCheck, FileText, Ticket as TicketIcon, History, Briefcase,
} from "lucide-react";
import { PageHeader } from "@/components/ui/PageHeader";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxBadge } from "@/components/ui/NxBadge";
import { NxAvatar } from "@/components/ui/NxAvatar";
import { Skeleton } from "@/components/ui/Skeleton";
import { apiGet } from "@/lib/api";
import {
    CUSTOMER_STATUS_VARIANT,
    CUSTOMER_TYPE_VARIANT,
    ORDER_STATUS_VARIANT,
    TICKET_STATUS_VARIANT,
} from "@/lib/rbac";
import { formatAOAFull, formatRel } from "@/lib/sla";
import pt from "@/lib/i18n";

const Gauge = ({ score = 0 }) => {
    const pct = Math.round((score || 0) * 100);
    const color =
        pct < 30 ? "var(--success)" : pct < 60 ? "var(--copper)" : "var(--error)";
    return (
        <div className="flex items-center gap-3">
            <div className="relative w-20 h-20" data-testid="churn-gauge">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                    <circle cx="18" cy="18" r="15" fill="none" stroke="var(--glass-border)" strokeWidth="3" />
                    <circle
                        cx="18" cy="18" r="15" fill="none"
                        stroke={color}
                        strokeWidth="3"
                        strokeDasharray={`${pct * 0.94} 100`}
                        strokeLinecap="round"
                    />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                    <span className="font-mono text-base text-[color:var(--slate-deep)]">{pct}%</span>
                </div>
            </div>
            <div>
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-[color:var(--slate-light)]">Risco de Churn</p>
                <p className="font-display text-sm text-[color:var(--slate-deep)] mt-0.5">
                    {pct < 30 ? (pt.customers.churn_stable || "Cliente estável") : pct < 60 ? (pt.customers.churn_attention || "Atenção") : (pt.customers.churn_highRisk || "Risco elevado")}
                </p>
            </div>
        </div>
    );
};

const TABS = [
    { key: "info", label: pt.customers.tabs.info },
    { key: "services", label: pt.customers.tabs.services },
    { key: "documents", label: pt.customers.tabs.documents },
    { key: "tickets", label: pt.customers.tabs.tickets },
];

const CustomerDetailPage = () => {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [tab, setTab] = useState("info");

    useEffect(() => {
        (async () => {
            try {
                const d = await apiGet(`/customers/${customerId}`);
                setData(d);
            } catch (e) {
                setData(null);
            }
        })();
    }, [customerId]);

    if (!data) {
        return (
            <div>
                <Skeleton height="40px" width="320px" />
                <div className="mt-6"><Skeleton height="240px" /></div>
            </div>
        );
    }

    const { customer, orders, documents, tickets } = data;

    return (
        <div data-testid="customer-detail-root">
            <button
                type="button"
                onClick={() => navigate("/clientes")}
                className="inline-flex items-center gap-1.5 text-sm text-[color:var(--slate-mid)] hover:text-[color:var(--copper-dark)] mb-4"
                data-testid="back-to-customers"
            >
                <ArrowLeft size={14} /> Voltar a clientes
            </button>

            {/* Header card */}
            <GlassCard padding="p-7" className="mb-5">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="flex items-center gap-4">
                        <NxAvatar name={customer.full_name} size="xl" />
                        <div>
                            <h1 className="font-display text-3xl text-[color:var(--slate-deep)] tracking-tight">{customer.full_name}</h1>
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <NxBadge variant={CUSTOMER_TYPE_VARIANT[customer.customer_type]}>
                                    {pt.customers.types[customer.customer_type]}
                                </NxBadge>
                                <NxBadge variant={CUSTOMER_STATUS_VARIANT[customer.status]}>
                                    {pt.customers.statuses[customer.status]}
                                </NxBadge>
                                {customer.document_verified && (
                                    <NxBadge variant="success"><BadgeCheck size={12} /> Documentos verificados</NxBadge>
                                )}
                            </div>
                        </div>
                    </div>
                    <Gauge score={customer.churn_risk_score} />
                </div>
            </GlassCard>

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-4 border-b border-[color:var(--glass-border)] overflow-x-auto">
                {TABS.map((t) => {
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setTab(t.key)}
                            className={
                                "px-4 py-2.5 font-body text-sm font-medium transition-colors border-b-2 " +
                                (active
                                    ? "border-[color:var(--copper)] text-[color:var(--copper-dark)]"
                                    : "border-transparent text-[color:var(--slate-mid)] hover:text-[color:var(--slate-deep)]")
                            }
                            data-testid={`customer-tab-${t.key}`}
                        >
                            {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "info" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="customer-tab-info-content">
                    <GlassCard padding="p-6">
                        <h3 className="font-display text-lg text-[color:var(--slate-deep)] mb-3">Contacto</h3>
                        <dl className="space-y-2 text-sm">
                            <div className="flex items-center gap-3"><Phone size={14} className="text-[color:var(--copper)]" /><span className="font-mono text-[color:var(--slate-deep)]">{customer.phone}</span></div>
                            {customer.email && <div className="flex items-center gap-3"><Mail size={14} className="text-[color:var(--copper)]" /><span className="font-mono text-[color:var(--slate-deep)]">{customer.email}</span></div>}
                            {customer.address_street && <div className="flex items-center gap-3"><MapPin size={14} className="text-[color:var(--copper)]" /><span className="text-[color:var(--slate-deep)]">{customer.address_street}, {customer.address_city}{customer.address_province ? ` · ${customer.address_province}` : ""}</span></div>}
                        </dl>
                    </GlassCard>
                    <GlassCard padding="p-6">
                        <h3 className="font-display text-lg text-[color:var(--slate-deep)] mb-3">Documentos identificativos</h3>
                        <dl className="space-y-2 text-sm">
                            {customer.bi_number && (
                                <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">BI</dt><dd className="font-mono text-[color:var(--slate-deep)]">{customer.bi_number}</dd></div>
                            )}
                            {customer.nif && (
                                <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">NIF</dt><dd className="font-mono text-[color:var(--slate-deep)]">{customer.nif}</dd></div>
                            )}
                            <div className="flex justify-between"><dt className="text-[color:var(--slate-mid)]">LTV</dt><dd className="font-mono text-[color:var(--copper-dark)]">AOA {formatAOAFull(customer.lifetime_value_aoa)}</dd></div>
                        </dl>
                    </GlassCard>
                </div>
            )}

            {tab === "services" && (
                <GlassCard padding="p-0" testId="customer-tab-services-content">
                    {orders.length === 0 ? (
                        <div className="p-6 text-sm text-[color:var(--slate-mid)]">{pt.common.noServices}</div>
                    ) : (
                        <ul className="divide-y divide-[color:var(--glass-border-subtle)]">
                            {orders.map((o) => (
                                <li key={o.id} onClick={() => navigate(`/servicos/${o.id}`)} className="p-4 cursor-pointer hover:bg-[color:var(--copper-subtle)] flex items-center justify-between">
                                    <div>
                                        <p className="font-mono text-xs text-[color:var(--copper-dark)]">{o.order_number}</p>
                                        <p className="font-body text-sm text-[color:var(--slate-deep)] mt-0.5">{o.notes || "{pt.common.serviceOrder}"}</p>
                                    </div>
                                    <NxBadge variant={ORDER_STATUS_VARIANT[o.status]}>{pt.services[`status_${o.status}`] || o.status}</NxBadge>
                                </li>
                            ))}
                        </ul>
                    )}
                </GlassCard>
            )}

            {tab === "documents" && (
                <GlassCard padding="p-0" testId="customer-tab-documents-content">
                    {documents.length === 0 ? (
                        <div className="p-6 text-sm text-[color:var(--slate-mid)]">{pt.common.noDocuments}</div>
                    ) : (
                        <ul className="divide-y divide-[color:var(--glass-border-subtle)]">
                            {documents.map((d) => (
                                <li key={d.id} className="p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="h-9 w-9 rounded-md bg-[color:var(--copper-subtle)] grid place-items-center"><FileText size={15} className="text-[color:var(--copper-dark)]" /></div>
                                        <div>
                                            <p className="font-body text-sm text-[color:var(--slate-deep)] font-medium">{d.file_name}</p>
                                            <p className="font-mono text-xs text-[color:var(--slate-mid)]">{d.document_type} · {formatRel(d.created_at)}</p>
                                        </div>
                                    </div>
                                    {d.is_verified ? <NxBadge variant="success">Verificado</NxBadge> : <NxBadge variant="warning">Pendente</NxBadge>}
                                </li>
                            ))}
                        </ul>
                    )}
                </GlassCard>
            )}

            {tab === "tickets" && (
                <GlassCard padding="p-0" testId="customer-tab-tickets-content">
                    {tickets.length === 0 ? (
                        <div className="p-6 text-sm text-[color:var(--slate-mid)]">{pt.common.noTickets}</div>
                    ) : (
                        <ul className="divide-y divide-[color:var(--glass-border-subtle)]">
                            {tickets.map((t) => (
                                <li key={t.id} onClick={() => navigate(`/tickets/${t.id}`)} className="p-4 cursor-pointer hover:bg-[color:var(--copper-subtle)] flex items-center justify-between">
                                    <div>
                                        <p className="font-mono text-xs text-[color:var(--copper-dark)]">{t.ticket_number}</p>
                                        <p className="font-body text-sm text-[color:var(--slate-deep)] mt-0.5">{t.subject}</p>
                                    </div>
                                    <NxBadge variant={TICKET_STATUS_VARIANT[t.status]}>{pt.tickets.statuses[t.status]}</NxBadge>
                                </li>
                            ))}
                        </ul>
                    )}
                </GlassCard>
            )}
        </div>
    );
};

export default CustomerDetailPage;
